import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'touch';
  isLoading?: boolean;
  fullWidth?: boolean;
  children: React.ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const variants = {
    primary:   'bg-primary-700 text-white hover:bg-primary-800 focus:ring-primary-500 shadow-md hover:shadow-lg',
    secondary: 'bg-slate-700 text-white hover:bg-slate-800 focus:ring-slate-500 shadow-md hover:shadow-lg',
    outline:   'border-2 border-slate-200 bg-white text-slate-700 hover:border-primary-400 hover:text-primary-700 focus:ring-primary-400',
    ghost:     'bg-transparent text-slate-600 hover:bg-slate-100 focus:ring-slate-400',
    danger:    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-md hover:shadow-lg',
  };

  const sizes = {
    sm:    'px-3 py-1.5 text-sm rounded-lg',
    md:    'px-4 py-2.5 text-sm rounded-xl',
    lg:    'px-6 py-3 text-base rounded-xl',
    touch: 'px-6 py-5 text-xl min-h-16 min-w-16 rounded-2xl font-bold',
  };

  return (
    <button
      className={clsx(
        'font-semibold transition-all duration-200 active:scale-95',
        'focus:outline-none focus:ring-2 focus:ring-opacity-50',
        variants[variant],
        sizes[size],
        fullWidth ? 'w-full' : '',
        (disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : '',
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span>Cargando...</span>
        </div>
      ) : children}
    </button>
  );
}
